import { apiClient, briefApiClient } from "./client";

export interface RealtimeTokenResponse {
  token: string;
  expiresIn: number;
}

/** Fetch a short-lived JWT authorizing one realtime WS scope (e.g.
 *  "presence:app"). The slumbr-realtime-worker verifies it on the WS upgrade. */
export async function getRealtimeToken(scope: string): Promise<RealtimeTokenResponse> {
  return apiClient.post<RealtimeTokenResponse>("/api/realtime/token", { scope });
}

/**
 * Card Table v2 friends-only tables: the host a table code's token should claim friendship with.
 * The table client registers it after TableDO answers `denied {reason:"friends_only", hostId}`
 * (and the backend confirmed the friendship); the transport's next token request carries it.
 */
const tableFriendHints = new Map<string, string>();
export function setTableFriendHint(code: string, hostId: string | null): void {
  if (hostId) tableFriendHints.set(code, hostId);
  else tableFriendHints.delete(code);
}

/**
 * Token for a Card Table v2 socket (scope `table2:{code}`, identity = active persona). With
 * `friendOf` — passed, or registered via setTableFriendHint — the backend checks the caller is
 * that user's friend and adds the `fr` claim; it answers 403 when they aren't.
 */
export async function getTable2RealtimeToken(code: string, friendOf?: string): Promise<RealtimeTokenResponse> {
  const fr = friendOf ?? tableFriendHints.get(code);
  const scope = `table2:${code}`;
  return apiClient.post<RealtimeTokenResponse>("/api/realtime/token", fr ? { scope, friendOf: fr } : { scope });
}

/** Per-page → parent-note hints. A nested page inherits realtime access from
 *  its root note (collaborators are shared the note, not each page), so the
 *  token request must carry the parent. The transport only hands us the page
 *  id, so the editor registers the parent here before joining. */
const noteParentHints = new Map<string, string>();
export function setNoteParentHint(pageId: string, parentId: string | null): void {
  if (parentId && parentId !== pageId) noteParentHints.set(pageId, parentId);
  else noteParentHints.delete(pageId);
}

/** Membership-checked realtime token for a note's collab Durable Object. The
 *  backend verifies the caller owns or has a share on the note (or its parent,
 *  for a nested page) before minting a `note:{id}` scope. */
export async function getNoteRealtimeToken(noteId: string): Promise<RealtimeTokenResponse> {
  const parentId = noteParentHints.get(noteId);
  return apiClient.post<RealtimeTokenResponse>("/api/notes/realtime-token", parentId ? { noteId, parentId } : { noteId });
}

/** Membership-checked realtime token for a community channel's Durable Object. */
export async function getCommunityRealtimeToken(
  communityId: string,
  channelId: string
): Promise<RealtimeTokenResponse> {
  return apiClient.post<RealtimeTokenResponse>("/api/communities/realtime-token", {
    communityId,
    channelId,
  });
}

/** Membership-checked realtime token for a Mimic room's Durable Object
 *  (scope `game:mm-{roomId}`). */
export async function getMimicRealtimeToken(roomId: string): Promise<RealtimeTokenResponse> {
  return apiClient.get<RealtimeTokenResponse>(
    `/v1/games/mimic/rooms/${encodeURIComponent(roomId)}/do-token`
  );
}

/** Membership-checked realtime token for a blackjack room's Durable Object. */
export async function getBlackjackRealtimeToken(roomId: string): Promise<RealtimeTokenResponse> {
  return apiClient.get<RealtimeTokenResponse>(
    `/v1/games/blackjack/rooms/${encodeURIComponent(roomId)}/do-token`
  );
}

/** Membership-checked realtime token for a Higher-or-Lower room's Durable Object. */
export async function getHiloRealtimeToken(roomId: string): Promise<RealtimeTokenResponse> {
  return apiClient.get<RealtimeTokenResponse>(
    `/v1/games/hilo/rooms/${encodeURIComponent(roomId)}/do-token`
  );
}

/** Membership-checked realtime token for a Quiplip room's Durable Object. */
export async function getQuiplipRealtimeToken(roomId: string): Promise<RealtimeTokenResponse> {
  return apiClient.get<RealtimeTokenResponse>(
    `/v1/games/quiplip/rooms/${encodeURIComponent(roomId)}/do-token`
  );
}

/** Membership-checked realtime token for a Texas Hold'em room's Durable Object. */
export async function getPokerRealtimeToken(roomId: string): Promise<RealtimeTokenResponse> {
  return apiClient.get<RealtimeTokenResponse>(
    `/v1/games/poker/rooms/${encodeURIComponent(roomId)}/do-token`
  );
}

/** Membership-checked realtime token for a Doodlr room's Durable Object. */
export async function getDoodlrRealtimeToken(roomId: string): Promise<RealtimeTokenResponse> {
  return apiClient.get<RealtimeTokenResponse>(
    `/v1/games/doodlr/rooms/${encodeURIComponent(roomId)}/do-token`
  );
}

/** Membership-checked realtime token for a Bluffr room's Durable Object. */
export async function getBluffrRealtimeToken(roomId: string): Promise<RealtimeTokenResponse> {
  return apiClient.get<RealtimeTokenResponse>(
    `/v1/games/bluffr/rooms/${encodeURIComponent(roomId)}/do-token`
  );
}

/** Membership-checked realtime token for a Brief board's Durable Object room.
 *  Brief is on its own HTTP API, so this uses the Brief-scoped client. */
export async function getBriefRealtimeToken(boardId: string): Promise<RealtimeTokenResponse> {
  return briefApiClient.post<RealtimeTokenResponse>("/api/brief/realtime-token", { boardId });
}
